import React, { CSSProperties, useEffect } from "react";
import clsx from "clsx";
import { withStyles, Theme, createStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Menu, { MenuProps } from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { v4 as uuidv4 } from "uuid";
import { UIStyleProps, UIStyledTooltip } from "./UIComponents";
import "./UIMenuBtn.scss";

export type UIMenuBtnProps = UIStyleProps & {
	menuId: string;
	title?: string | React.ReactNode;
	options: IMenuOption[];
	children?: React.ReactNode;
	invertedColors?: boolean;
	menuIconBtn?: boolean;
	btnClassName?: string;
	btnVariant?: "contained" | "text" | "outlined";
	btnStyle?: CSSProperties;
	menuClassName?: string;
};

export type IMenuOption = {
	title?: string;
	optionType?: "heading" | "separator";
	actionProcessingTitle?: string;
	icon?: React.ReactElement;
	content?: React.ReactNode;
	isBtnItem?: boolean;
	onClick?: () => Promise<any>;
	onSuccess?: (option: IMenuOption) => any;
	closeOnSuccess?: boolean;
};

const menuStyles = function (theme: Theme) {
	return createStyles({
		paper: {
			backgroundColor: "var(--color-contrast-bg)",
			boxShadow: theme.shadows[3],
			padding: theme.spacing(2),
		},
		list: {
			padding: theme.spacing(0),
			color: "var(--color-contrast-text)",
		},
		button: {
			paddingLeft: "10px",
			paddingRight: "10px",
			color: "var(--color-contrast-text)",
		},
	});
};

const StyledMenu = withStyles(menuStyles)((props: MenuProps) => (
	<Menu
		className={props.className}
		elevation={1}
		autoFocus={false}
		// PopoverClasses={menuStyles.list}
		variant="menu"
		getContentAnchorEl={null}
		anchorOrigin={{
			vertical: "bottom",
			horizontal: "right",
		}}
		transformOrigin={{
			vertical: "top",
			horizontal: "right",
		}}
		{...props}
	/>
));

const StyledMenuItem = withStyles((theme) => ({
	root: {
		// "&:focus": {
		// 	backgroundColor: theme.palette.primary.main,
		// 	"& .MuiListItemIcon-root, & .MuiListItemText-primary": {
		// 		color: theme.palette.common.white
		// 	}
		// }
	},
}))(MenuItem);

export const UIMenuBtn: React.FC<UIMenuBtnProps> = (props: UIMenuBtnProps) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const [actionsState, setActionsState] = React.useState<{ [index: number]: boolean }>({});
	const [refresh, setRefresh] = React.useState(0);
	const id: string = "customized-menu-" + props.menuId;

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const onMenuItemClick = (option: IMenuOption, index: number) => {
		console.log("onMenuItemClick() option:", option);
		if (!option.onClick) return;
		const state = actionsState || {};
		state[index] = true;
		setActionsState(state);
		// setRefresh(refresh + 1);
		option
			.onClick()
			.then(() => {
				if (option.onSuccess) option.onSuccess(option);
				if (option.closeOnSuccess) handleClose();
			})
			.finally(() => {
				console.log("onMenuItemClick():  option.onClick() complete");
				const state = actionsState || {};
				state[index] = false;
				setActionsState(state);
				// setRefresh(refresh + 2);
			});
	};

	return (
		<div
			className={clsx(
				"ui-menu-btn-container",
				props.className,
				props.invertedColors && !props.menuIconBtn && "ui-menu-btn-inverted"
			)}
			style={props.style}
		>
			{props.menuIconBtn ? (
				<UIStyledTooltip title={props.title} placement="right-start">
					<IconButton
						edge="start"
						size="small"
						color="primary"
						onClick={handleClick}
						className={clsx(props.btnClassName, "text-xs")}
					>
						<i className="im im-menu-list" style={{ fontSize: "11px" }}></i>
					</IconButton>
				</UIStyledTooltip>
			) : (
				<Button
					aria-controls={id}
					aria-haspopup="true"
					variant={props.btnVariant || "contained"}
					color="default"
					onClick={handleClick}
					className={clsx("btn", props.btnClassName, "py-1")}
					style={props.style}
				>
					<span className="d-flex align-items-center">
						{props.title}
						<i
							className="im im-care-down"
							style={{ fontSize: "10px", marginLeft: "15px", marginRight: "4px", marginBottom: "-2px" }}
						></i>
					</span>
				</Button>
			)}

			<StyledMenu
				id={id}
				className={props.menuClassName}
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				disableRestoreFocus={true}
				onClose={handleClose}
				transitionDuration={{ enter: 200, exit: 100 }}
			>
				{props.children}
				{props.options.map((option: IMenuOption, index: number) => {
					const processing: boolean = actionsState[index];
					return option.optionType === "heading" ? (
						<h4 className="mb-1" key={uuidv4()}>
							{option.title}
						</h4>
					) : option.optionType === "separator" ? (
						<hr key={uuidv4()} />
					) : (
						<StyledMenuItem
							title={processing ? option.actionProcessingTitle || "Processing..." : option.title || ""}
							// key={index + refresh + option.title}
							key={uuidv4()}
							onClick={
								processing
									? undefined
									: (e) => {
											e.preventDefault();
											e.stopPropagation();
											onMenuItemClick(option, index);
											setAnchorEl(null);
									  }
							}
							className={clsx(
								"ui-menu-btn-item",
								option.isBtnItem && "btn-list-item",
								processing && "progress-text"
							)}
						>
							{option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
							{option.content || <ListItemText primary={option.title} />}
						</StyledMenuItem>
					);
				})}
			</StyledMenu>
		</div>
	);
};

export default UIMenuBtn;
